import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Lock, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Parse hash params (Supabase recovery links put tokens in the URL hash)
  const parseHashParams = () => {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    return { access_token, refresh_token };
  };

  // Ensure the recovery session is established (handles "Auth session missing")
  useEffect(() => {
    const establishSession = async () => {
      setStatus('');

      // 1) Try to pick up existing session
      const { data: existingSession } = await supabase.auth.getSession();
      if (existingSession?.session) {
        setSessionReady(true);
        return;
      }

      // 2) Try hash-based tokens (access_token/refresh_token)
      const { access_token, refresh_token } = parseHashParams();
      if (access_token && refresh_token) {
        const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
        if (!error && data?.session) {
          setSessionReady(true);
          return;
        }
      }

      // 3) Try code/token in query (PKCE-style)
      const url = new URL(window.location.href);
      const token = url.searchParams.get('token') || url.searchParams.get('code');

      if (token) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(token);
        if (!error && data?.session) {
          setSessionReady(true);
          return;
        }
      }

      // 4) Otherwise, session could not be established
      setStatus('Password reset link is missing or expired. Please request a new link.');
    };

    establishSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');

    if (password !== confirm) {
      setStatus('Passwords do not match.');
      return;
    }

    if (!sessionReady) {
      setStatus('Password reset link is missing or expired. Please request a new link.');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setStatus(error.message || 'Could not update password. Please try again.');
      } else {
        setStatus('Password updated. You can log in now.');
        setTimeout(() => navigate('/login'), 1200);
      }
    } catch (err) {
      setStatus('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-8">
        <Link to="/" className="flex items-center text-gray-600 hover:text-primary-600 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
        <p className="text-gray-600">
          Enter a new password for your account.
        </p>
      </div>

      <div className="card">
        {status && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
            {status}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pl-10"
                placeholder="Enter a new password"
                required
                minLength={6}
              />
            </div>
          </div>

          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="password"
                id="confirm"
                name="confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input-field pl-10"
                placeholder="Confirm your new password"
                required
                minLength={6}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;

